import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Moon, Sun } from 'lucide-react';

import { Theme } from '@/shared/config';
import { cn, useTheme } from '@/shared/libs';

import type { AppShellProps, AppShellTab } from '../../model/app-shell-tab';
import { LanguageSwitcher } from '../LanguageSwitcher/LanguageSwitcher';
import { UserMenu } from '../UserMenu/UserMenu';

const BADGE_TONE_CLASS: Record<
  NonNullable<AppShellTab['badgeTone']>,
  string
> = {
  amber: 'bg-amber-500',
  rose: 'bg-rose-500',
};

const AppShellLogo: FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <div
        className={cn(
          'brand-gradient flex shrink-0 items-center justify-center rounded-xl shadow-lg shadow-brand/30',
          compact ? 'size-7 rounded-lg' : 'size-9',
        )}>
        <Layers
          className={cn('text-brand-foreground', compact ? 'size-4' : 'size-5')}
        />
      </div>
      <span className="truncate text-sm font-semibold tracking-tight">
        {t('app.name')}
      </span>
    </div>
  );
};

AppShellLogo.displayName = 'AppShellLogo';

const SidebarNavItem: FC<{
  isActive: boolean;
  onSelect: () => void;
  tab: AppShellTab;
}> = ({ isActive, onSelect, tab }) => {
  const Icon = tab.icon;

  return (
    <button
      className={cn('nav-item group w-full', isActive && 'nav-item-active')}
      type="button"
      onClick={onSelect}>
      <Icon
        className={cn(
          'size-4 transition-transform',
          isActive ? 'scale-110' : 'group-hover:scale-105',
        )}
      />
      {tab.label}
      {!!tab.badge && (
        <span
          className={cn(
            'ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] leading-none font-semibold text-white',
            BADGE_TONE_CLASS[tab.badgeTone ?? 'rose'],
          )}>
          {tab.badge > 99 ? '99+' : tab.badge}
        </span>
      )}
    </button>
  );
};

SidebarNavItem.displayName = 'SidebarNavItem';

export const AppShell: FC<AppShellProps> = ({
  activeTab,
  children,
  onTabChange,
  tabs,
}) => {
  const { t } = useTranslation();
  const { changeTheme, theme } = useTheme();

  const handleToggleTheme = useCallback(() => {
    changeTheme(theme === Theme.Dark ? Theme.Light : Theme.Dark);
  }, [changeTheme, theme]);

  return (
    <div className="flex h-dvh flex-col overflow-x-clip text-foreground transition-colors duration-500 md:min-h-dvh md:h-auto">
      <header className="glass-panel flex shrink-0 items-center justify-between border-b px-4 py-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] md:hidden">
        <AppShellLogo compact />
        <div className="shrink-0">
          <UserMenu />
        </div>
      </header>

      <div className="flex min-h-0 min-w-0 flex-1">
        <aside className="glass-panel sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r px-5 py-6 md:flex">
          <div className="mb-9">
            <AppShellLogo />
          </div>

          <nav className="flex flex-1 flex-col gap-1">
            {tabs.map((tab) => (
              <SidebarNavItem
                key={tab.key}
                isActive={tab.key === activeTab}
                tab={tab}
                onSelect={() => onTabChange(tab.key)}
              />
            ))}
          </nav>

          <div className="mt-4 space-y-3 border-t border-border/40 pt-4">
            <LanguageSwitcher />

            <button
              className="nav-item w-full justify-between"
              type="button"
              onClick={handleToggleTheme}>
              <span className="flex items-center gap-3">
                {theme === Theme.Dark ? (
                  <Sun className="size-4" />
                ) : (
                  <Moon className="size-4" />
                )}
                {theme === Theme.Dark ? t('theme.light') : t('theme.dark')}
              </span>
            </button>

            <UserMenu variant="sidebar" />
          </div>
        </aside>

        <main className="min-h-0 min-w-0 flex-1 overflow-x-clip overflow-y-auto px-5 py-6 md:px-10 md:py-10">
          {children}
        </main>
      </div>

      <nav className="glass-panel grid shrink-0 auto-cols-fr grid-flow-col overflow-x-clip border-t bg-background/80 px-1 py-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] md:hidden">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.key === activeTab;

          return (
            <button
              key={tab.key}
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-lg py-1.5 transition-all',
                isActive
                  ? 'text-brand dark:text-brand-muted'
                  : 'text-muted-foreground',
              )}
              type="button"
              onClick={() => onTabChange(tab.key)}>
              <span className="relative shrink-0">
                <Icon
                  className={cn(
                    'size-5 transition-transform',
                    isActive && 'scale-110',
                  )}
                />
                {!!tab.badge && (
                  <span
                    className={cn(
                      'absolute -top-0.5 -right-0.5 size-2 rounded-full',
                      BADGE_TONE_CLASS[tab.badgeTone ?? 'rose'],
                    )}
                  />
                )}
              </span>
              <span className="max-w-full truncate px-0.5 text-[10px] leading-none font-medium">
                {tab.label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

AppShell.displayName = 'AppShell';
